import React, { useState, useRef, useEffect } from 'react';

export interface SelectedFile {
  name: string;
  s3_key: string;
}

export interface FileItem {
  id: string;
  name: string;
  type: string;
  size?: number;
  s3_path?: string;
  parent_id: string;
}

export interface Workspace {
  id: string;
  name: string;
  created_date: string;
  description: string;
  owner: string;
}

export interface StepFilesProps {
  nodeFiles: SelectedFile[];
  edgeFiles: SelectedFile[];
  onRemoveNodeFile: (idx: number) => void;
  onRemoveEdgeFile: (idx: number) => void;
  onClearNodeFiles: () => void;
  onClearEdgeFiles: () => void;
  nodeFilesError: string;
  onAssignFiles: (files: SelectedFile[], bucket: 'node' | 'edge') => void;

  selectedWorkspace: Workspace | null;
  workspaces: Workspace[];
  wsLoading: boolean;
  wsError: string;
  wsTotal: number;
  wsFrom: number;
  WS_PAGE: number;
  onWorkspaceSelect: (ws: Workspace) => void;
  onLoadWorkspaces: (query: string, from: number) => void;

  files: FileItem[];
  fileLoading: boolean;
  fileError: string;
  fileTotal: number;
  fileFrom: number;
  FILE_PAGE: number;
  breadcrumb: { id: string; name: string }[];
  onFolderClick: (folder: FileItem) => void;
  onBreadcrumbClick: (idx: number) => void;
  onLoadFiles: (wsId: string, parentId: string, from: number) => void;
  currentParentId: string;
  onSelectAll: (onLoaded: (files: FileItem[]) => void) => void;
  selectingAll: boolean;
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isFileSelectable(file: FileItem): boolean {
  return file.type !== 'folder' && file.name.toLowerCase().endsWith('.csv');
}

// SVG icon components
function FolderIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
      <path d="M1.5 3.5C1.5 2.948 1.948 2.5 2.5 2.5H6.086a1 1 0 0 1 .707.293L7.5 3.5H13.5c.552 0 1 .448 1 1V12.5c0 .552-.448 1-1 1h-11a1 1 0 0 1-1-1V3.5z" fill="currentColor" opacity=".18"/>
      <path d="M1.5 5.5h13v7a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-7z" fill="currentColor" opacity=".35"/>
      <path d="M1.5 3.5C1.5 2.948 1.948 2.5 2.5 2.5H6.086a1 1 0 0 1 .707.293L7.5 3.5h-6v0zM1.5 5.5h13M1.5 5.5V12.5a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V5.5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" fill="none" opacity=".7"/>
    </svg>
  );
}

function FileIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
      <path d="M3.5 1.5h6l3 3V14a.5.5 0 0 1-.5.5h-8.5a.5.5 0 0 1-.5-.5V2a.5.5 0 0 1 .5-.5z" fill="currentColor" opacity=".12"/>
      <path d="M3.5 1.5h6l3 3V14a.5.5 0 0 1-.5.5h-8.5a.5.5 0 0 1-.5-.5V2a.5.5 0 0 1 .5-.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
      <path d="M9.5 1.5v3h3" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
      <path d="M5.5 7.5h5M5.5 9.5h5M5.5 11.5h3" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
    </svg>
  );
}

function WorkspaceIcon({ size = 14, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
      <rect x="1.5" y="4.5" width="13" height="10" rx="1" stroke="currentColor" strokeWidth="1.2"/>
      <path d="M1.5 7.5h13" stroke="currentColor" strokeWidth="1.2"/>
      <path d="M5.5 1.5h5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M3.5 4.5V2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M12.5 4.5V2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  );
}

function ChevronDownIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function ChevronRightIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.5 2L6.5 5L3.5 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function WarningIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 2L14.5 13.5H1.5L8 2z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
      <path d="M8 6.5v3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      <circle cx="8" cy="11" r=".7" fill="currentColor"/>
    </svg>
  );
}

const StepFiles: React.FC<StepFilesProps> = ({
  nodeFiles, edgeFiles,
  onRemoveNodeFile, onRemoveEdgeFile, onClearNodeFiles, onClearEdgeFiles,
  nodeFilesError, onAssignFiles,
  selectedWorkspace, workspaces, wsLoading, wsError, wsTotal, wsFrom, WS_PAGE,
  onWorkspaceSelect, onLoadWorkspaces,
  files, fileLoading, fileError, fileTotal, fileFrom, FILE_PAGE,
  breadcrumb, onFolderClick, onBreadcrumbClick, onLoadFiles, currentParentId,
  onSelectAll, selectingAll,
}) => {
  const [wsOpen, setWsOpen] = useState(false);
  const [wsQuery, setWsQuery] = useState('');
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  // Full FileItem map for all checked files (spans pages)
  const checkedFilesRef = useRef<Map<string, FileItem>>(new Map());
  const [fileSearch, setFileSearch] = useState('');
  const wsDropdownRef = useRef<HTMLDivElement>(null);
  const wsBtnRef = useRef<HTMLButtonElement>(null);
  const wsSearchRef = useRef<HTMLInputElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!wsOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        wsDropdownRef.current && !wsDropdownRef.current.contains(e.target as Node) &&
        wsBtnRef.current && !wsBtnRef.current.contains(e.target as Node)
      ) {
        setWsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [wsOpen]);

  // Auto-focus workspace search when dropdown opens
  useEffect(() => {
    if (wsOpen) setTimeout(() => wsSearchRef.current?.focus(), 50);
  }, [wsOpen]);

  // Live workspace search with debounce
  useEffect(() => {
    if (!wsOpen) return;
    const timer = setTimeout(() => onLoadWorkspaces(wsQuery, 0), 280);
    return () => clearTimeout(timer);
  }, [wsQuery, wsOpen, onLoadWorkspaces]);

  const handleWsToggle = () => {
    if (!wsOpen) {
      setWsQuery('');
      onLoadWorkspaces('', 0);
    }
    setWsOpen(v => !v);
  };

  const handleWsSelect = (ws: Workspace) => {
    onWorkspaceSelect(ws);
    setWsOpen(false);
    setWsQuery('');
    setCheckedIds(new Set());
    checkedFilesRef.current.clear();
  };

  const handleToggleFile = (file: FileItem) => {
    if (!isFileSelectable(file)) return;
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(file.id)) {
        next.delete(file.id);
        checkedFilesRef.current.delete(file.id);
      } else {
        next.add(file.id);
        checkedFilesRef.current.set(file.id, file);
      }
      return next;
    });
  };

  const handleAssign = (bucket: 'node' | 'edge') => {
    const toAdd: SelectedFile[] = Array.from(checkedFilesRef.current.values())
      .filter(f => f.s3_path)
      .map(f => ({ name: f.name, s3_key: f.s3_path! }));
    onAssignFiles(toAdd, bucket);
    setCheckedIds(new Set());
    checkedFilesRef.current.clear();
  };

  const displayFiles = fileSearch
    ? files.filter(f => f.name.toLowerCase().includes(fileSearch.toLowerCase()))
    : files;

  const checkedCount = checkedIds.size;

  return (
    <div className="wiz-files-layout" style={{ height: '100%' }}>

      {/* Left: browser */}
      <div className="wiz-browser">

        {/* Workspace selector row */}
        <div className="wiz-ws-row" style={{ position: 'relative' }}>
          <span className="wiz-ws-label">Workspace</span>

          {selectedWorkspace ? (
            <>
              <button
                ref={wsBtnRef}
                className="wiz-ws-btn"
                onClick={handleWsToggle}
                title={selectedWorkspace.name}
              >
                <WorkspaceIcon size={13} style={{ flexShrink: 0, opacity: 0.7 }} />
                {selectedWorkspace.name}
              </button>
              <button className="wiz-ws-change" onClick={handleWsToggle}>Change</button>
            </>
          ) : (
            <button
              ref={wsBtnRef}
              className="wiz-ws-btn wiz-ws-btn--placeholder"
              onClick={handleWsToggle}
            >
              Select workspace
              <ChevronDownIcon size={10} />
            </button>
          )}

          {wsOpen && (
            <div className="wiz-ws-dropdown" ref={wsDropdownRef}
              style={{ top: 34, left: 72 }}>
              <div className="wiz-ws-dropdown-search">
                <input
                  ref={wsSearchRef}
                  className="wiz-ws-search-input"
                  placeholder="Search workspaces..."
                  value={wsQuery}
                  onChange={e => setWsQuery(e.target.value)}
                />
              </div>
              <div className="wiz-ws-dropdown-list">
                {wsLoading ? (
                  [0,1,2].map(i => (
                    <div key={i} className="wiz-skeleton-row">
                      <div className="wiz-skeleton-block" style={{ width: 20, height: 20, borderRadius: '50%' }} />
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div className="wiz-skeleton-block" style={{ width: '70%', height: 12 }} />
                        <div className="wiz-skeleton-block" style={{ width: '45%', height: 10 }} />
                      </div>
                    </div>
                  ))
                ) : wsError ? (
                  <div className="wiz-ws-empty" style={{ color: 'var(--destructive)' }}>{wsError}</div>
                ) : workspaces.length === 0 ? (
                  <div className="wiz-ws-empty">No workspaces found</div>
                ) : (
                  workspaces.map(ws => (
                    <div
                      key={ws.id}
                      className={`wiz-ws-item${selectedWorkspace?.id === ws.id ? ' wiz-ws-item--active' : ''}`}
                      onClick={() => handleWsSelect(ws)}
                    >
                      <span className="wiz-ws-item-icon"><WorkspaceIcon size={14} /></span>
                      <div className="wiz-ws-item-info">
                        <div className="wiz-ws-item-name">{ws.name}</div>
                        {ws.owner && <div className="wiz-ws-item-meta">{ws.owner}</div>}
                      </div>
                    </div>
                  ))
                )}
              </div>
              {wsTotal > WS_PAGE && !wsLoading && (
                <div className="wiz-ws-pagination">
                  <button className="wiz-ws-pag-btn"
                    disabled={wsFrom === 0}
                    onClick={() => onLoadWorkspaces(wsQuery, Math.max(0, wsFrom - WS_PAGE))}>
                    Prev
                  </button>
                  <span className="wiz-ws-pag-info">{wsFrom + 1}-{Math.min(wsFrom + WS_PAGE, wsTotal)} of {wsTotal}</span>
                  <button className="wiz-ws-pag-btn"
                    disabled={wsFrom + WS_PAGE >= wsTotal}
                    onClick={() => onLoadWorkspaces(wsQuery, wsFrom + WS_PAGE)}>
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* File browser pane */}
        <div className="wiz-file-pane">
          <div className="wiz-file-pane-toolbar">
            <input
              className="wiz-file-search"
              placeholder="Filter files..."
              value={fileSearch}
              onChange={e => setFileSearch(e.target.value)}
              disabled={!selectedWorkspace || fileLoading}
            />
            <div className="wiz-file-breadcrumb">
              {breadcrumb.length === 0 && !selectedWorkspace && (
                <span style={{ fontSize: 12, color: 'var(--muted-foreground)', fontFamily: 'var(--font-primary)' }}>
                  Select a workspace to browse files
                </span>
              )}
              {breadcrumb.map((crumb, idx) => (
                <React.Fragment key={crumb.id}>
                  {idx < breadcrumb.length - 1 ? (
                    <span className="wiz-bc-item" onClick={() => onBreadcrumbClick(idx)}>{crumb.name}</span>
                  ) : (
                    <span className="wiz-bc-current">{crumb.name}</span>
                  )}
                  {idx < breadcrumb.length - 1 && <span className="wiz-bc-sep"><ChevronRightIcon size={10} /></span>}
                </React.Fragment>
              ))}
            </div>
            {selectedWorkspace && fileTotal > 0 && (
              <button
                className="wiz-file-selall-btn"
                onClick={() => onSelectAll(loaded => {
                  const selectable = loaded.filter(isFileSelectable);
                  const ids = new Set(selectable.map(f => f.id));
                  checkedFilesRef.current.clear();
                  selectable.forEach(f => checkedFilesRef.current.set(f.id, f));
                  setCheckedIds(ids);
                })}
                disabled={selectingAll || fileLoading}
              >
                {selectingAll ? 'Loading...' : 'Select all'}
              </button>
            )}
          </div>

          <div className="wiz-file-list">
            {!selectedWorkspace ? (
              <div className="wiz-file-empty">
                <span className="wiz-file-empty-icon"><WorkspaceIcon size={24} /></span>
                <span className="wiz-file-empty-text">Choose a workspace above to browse files</span>
              </div>
            ) : fileLoading ? (
              [0,1,2,3,4].map(i => (
                <div key={i} className="wiz-skeleton-row">
                  <div className="wiz-skeleton-block" style={{ width: 16, height: 16, borderRadius: 2 }} />
                  <div className="wiz-skeleton-block" style={{ width: '60%', height: 12 }} />
                  <div className="wiz-skeleton-block" style={{ width: '15%', height: 10, marginLeft: 'auto' }} />
                </div>
              ))
            ) : fileError ? (
              <div className="wiz-file-empty">
                <span className="wiz-file-empty-icon" style={{ color: 'var(--destructive)' }}><WarningIcon size={24} /></span>
                <span className="wiz-file-empty-text" style={{ color: 'var(--destructive)' }}>{fileError}</span>
              </div>
            ) : displayFiles.length === 0 ? (
              <div className="wiz-file-empty">
                <span className="wiz-file-empty-icon"><FolderIcon size={24} /></span>
                <span className="wiz-file-empty-text">
                  {fileSearch ? `No files match "${fileSearch}"` : 'This folder is empty'}
                </span>
              </div>
            ) : (
              displayFiles.map(file => {
                const selectable = isFileSelectable(file);
                const isFolder = file.type === 'folder';
                const isChecked = checkedIds.has(file.id);
                return (
                  <div
                    key={file.id}
                    className={[
                      'wiz-file-row',
                      isChecked ? 'wiz-file-row--selected' : '',
                      !selectable && !isFolder ? 'wiz-file-row--disabled' : '',
                      isFolder ? 'wiz-file-row--folder' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => {
                      if (isFolder) { onFolderClick(file); setCheckedIds(new Set()); }
                      else if (selectable) handleToggleFile(file);
                    }}
                    title={!selectable && !isFolder ? 'Only CSV files are supported' : undefined}
                  >
                    {selectable ? (
                      <input
                        type="checkbox"
                        className="wiz-file-cb"
                        checked={isChecked}
                        onChange={() => handleToggleFile(file)}
                        onClick={e => e.stopPropagation()}
                      />
                    ) : (
                      <span style={{ width: 14, flexShrink: 0 }} />
                    )}
                    <span className="wiz-file-icon">
                      {isFolder
                        ? <FolderIcon size={14} />
                        : <FileIcon size={14} />
                      }
                    </span>
                    <span className="wiz-file-name" title={file.name}>{file.name}</span>
                    {!selectable && !isFolder && <span className="wiz-file-notcsv">Not CSV</span>}
                    {file.size != null && selectable && (
                      <span className="wiz-file-size">{formatFileSize(file.size)}</span>
                    )}
                    {isFolder && <span className="wiz-file-chevron"><ChevronRightIcon size={10} /></span>}
                  </div>
                );
              })
            )}
          </div>

          {checkedCount > 0 && (
            <div className="wiz-assign-bar">
              <span className="wiz-assign-label">
                {checkedCount} file{checkedCount !== 1 ? 's' : ''}, add to:
              </span>
              <button className="wiz-assign-btn" onClick={() => handleAssign('node')}>Node files</button>
              <button className="wiz-assign-btn" onClick={() => handleAssign('edge')}>Edge files</button>
              <button className="wiz-assign-clear" onClick={() => setCheckedIds(new Set())}>Clear selection</button>
            </div>
          )}

          {fileTotal > FILE_PAGE && !fileLoading && selectedWorkspace && (
            <div className="wiz-file-pagination">
              <button className="wiz-file-pag-btn"
                disabled={fileFrom === 0}
                onClick={() => onLoadFiles(selectedWorkspace.id, currentParentId, Math.max(0, fileFrom - FILE_PAGE))}>
                Previous
              </button>
              <span className="wiz-file-pag-info">
                {fileFrom + 1}-{Math.min(fileFrom + FILE_PAGE, fileTotal)} of {fileTotal}
              </span>
              <button className="wiz-file-pag-btn"
                disabled={fileFrom + FILE_PAGE >= fileTotal}
                onClick={() => onLoadFiles(selectedWorkspace.id, currentParentId, fileFrom + FILE_PAGE)}>
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right rail: buckets */}
      <div className="wiz-buckets">
        {/* Node files */}
        <div className="wiz-bucket wiz-bucket--required">
          <div className="wiz-bucket-hd">
            <span className="wiz-bucket-title">
              Node files <span className="wiz-bucket-req">*</span>
            </span>
            {nodeFiles.length > 0 && (
              <>
                <span className="wiz-bucket-count">{nodeFiles.length}</span>
                <button className="wiz-bucket-clear" onClick={onClearNodeFiles}>Clear</button>
              </>
            )}
          </div>
          {nodeFiles.length === 0 ? (
            <div className="wiz-bucket-empty">
              <div className="wiz-bucket-dropzone">Select CSV files and click "Node files"</div>
            </div>
          ) : (
            <ul className="wiz-bucket-list">
              {nodeFiles.map((f, i) => (
                <li key={f.s3_key} className="wiz-bucket-item">
                  <span className="wiz-bucket-item-icon"><FileIcon size={12} /></span>
                  <span className="wiz-bucket-item-name" title={f.s3_key}>{f.name}</span>
                  <button className="wiz-bucket-rm" onClick={() => onRemoveNodeFile(i)}>x</button>
                </li>
              ))}
            </ul>
          )}
          {nodeFilesError && <div className="wiz-bucket-err-msg">{nodeFilesError}</div>}
          <div className="wiz-bucket-hint">CSV with an :ID column (required)</div>
        </div>

        {/* Edge files */}
        <div className="wiz-bucket">
          <div className="wiz-bucket-hd">
            <span className="wiz-bucket-title">Edge files</span>
            {edgeFiles.length > 0 && (
              <>
                <span className="wiz-bucket-count">{edgeFiles.length}</span>
                <button className="wiz-bucket-clear" onClick={onClearEdgeFiles}>Clear</button>
              </>
            )}
          </div>
          {edgeFiles.length === 0 ? (
            <div className="wiz-bucket-empty">
              <div className="wiz-bucket-dropzone">Select CSV files and click "Edge files"</div>
              <span className="wiz-bucket-optional">Optional</span>
            </div>
          ) : (
            <ul className="wiz-bucket-list">
              {edgeFiles.map((f, i) => (
                <li key={f.s3_key} className="wiz-bucket-item">
                  <span className="wiz-bucket-item-icon"><FileIcon size={12} /></span>
                  <span className="wiz-bucket-item-name" title={f.s3_key}>{f.name}</span>
                  <button className="wiz-bucket-rm" onClick={() => onRemoveEdgeFile(i)}>x</button>
                </li>
              ))}
            </ul>
          )}
          <div className="wiz-bucket-hint">CSV with :START_ID and :END_ID (optional)</div>
        </div>
      </div>
    </div>
  );
};

export default StepFiles;
